// Admin CoursesPage — paginated course catalog (admin sees drafts too, per
// the backend's role-aware GET /api/courses), inline "Create course" form,
// and a Published/Draft toggle per row.
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFetch, postJson, patchJson } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import { DELIVERY_MODES } from "@dm-lms/shared/types";
import type { Course, DeliveryMode, PaginatedResult } from "@dm-lms/shared/types";
import type { ActiveTutor } from "../../lib/adminTutorTypes.js";

const inputClass =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

const emptyForm = {
  title:          "",
  description:    "",
  tutorId:        "",
  mode:           "" as DeliveryMode | "",
  capacity:       "",
  durationHours:  "",
  price:          "",
};

export default function CoursesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useFetch<PaginatedResult<Course>>(`/api/courses?page=${page}&pageSize=20`);
  const { data: tutorsData } = useFetch<PaginatedResult<ActiveTutor>>("/api/admin/tutors/active?page=1&pageSize=100");

  const [courses, setCourses] = useState<Course[]>([]);
  useEffect(() => {
    if (data) setCourses(data.items);
  }, [data]);

  const tutorNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const tutor of tutorsData?.items ?? []) map.set(tutor.userId, tutor.name);
    return map;
  }, [tutorsData]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  async function createCourse(event: FormEvent) {
    event.preventDefault();
    if (!form.tutorId || !form.mode) return;
    setCreateError(null);
    setCreating(true);

    const result = await postJson<Course>("/api/courses", {
      title:          form.title,
      description:    form.description,
      tutorId:        form.tutorId,
      mode:           form.mode,
      capacity:       Number(form.capacity),
      durationHours:  Number(form.durationHours),
      price:          Number(form.price),
      prerequisiteId: null,
    });

    setCreating(false);
    if (!result.ok) {
      setCreateError(result.error);
      return;
    }
    setCourses((prev) => [result.data, ...prev]);
    setForm(emptyForm);
  }

  async function togglePublished(course: Course) {
    setToggleError(null);
    setTogglingId(course.id);
    const result = await patchJson<Course>(`/api/courses/${course.id}`, { isPublished: !course.isPublished });
    setTogglingId(null);
    if (!result.ok) {
      setToggleError(result.error);
      return;
    }
    setCourses((prev) => prev.map((c) => (c.id === course.id ? result.data : c)));
  }

  return (
    <div className="dm-page">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-dm-navy">Courses</h1>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white hover:bg-dm-blue"
        >
          {showForm ? "Cancel" : "Create course"}
        </button>
      </div>

      {isLoading && <p className="text-dm-slate text-sm">Loading courses…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {toggleError && <p className="text-red-600 text-sm mb-3">{toggleError}</p>}

      {!isLoading && !error && data && (
        <>
          {courses.length === 0 ? (
            <p className="text-dm-slate text-sm">No courses yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Tutor</th>
                    <th className="px-4 py-3 font-medium">Mode</th>
                    <th className="px-4 py-3 font-medium">Capacity</th>
                    <th className="px-4 py-3 font-medium">Price (KES)</th>
                    <th className="px-4 py-3 font-medium">Published</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {courses.map((course) => (
                    <tr
                      key={course.id}
                      onClick={() => { void navigate(`/app/admin/courses/${course.id}`); }}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 font-medium text-dm-navy">{course.code}</td>
                      <td className="px-4 py-3">{course.title}</td>
                      <td className="px-4 py-3">{tutorNameById.get(course.tutorId) ?? "—"}</td>
                      <td className="px-4 py-3">
                        <Badge tone="gray">{course.mode}</Badge>
                      </td>
                      <td className="px-4 py-3">{course.capacity}</td>
                      <td className="px-4 py-3">{course.priceKes?.toLocaleString() ?? "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          disabled={togglingId === course.id}
                          onClick={(e) => { e.stopPropagation(); void togglePublished(course); }}
                          className="disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Badge tone={course.isPublished ? "green" : "gray"}>
                            {course.isPublished ? "Published" : "Draft"}
                          </Badge>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/app/admin/sessions?courseId=${course.id}`}
                          onClick={(e) => { e.stopPropagation(); }}
                          className="text-xs font-medium text-dm-blue hover:underline"
                        >
                          View sessions
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-xs text-dm-slate">
              Page {data.page} · {data.total} total
            </span>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
              disabled={!data.hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showForm && (
        <form onSubmit={createCourse} className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-medium text-dm-navy">Create course</h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-dm-navy">Title</label>
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-dm-navy">Description</label>
              <textarea
                required
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dm-navy">Tutor</label>
              <select
                required
                value={form.tutorId}
                onChange={(e) => setForm((f) => ({ ...f, tutorId: e.target.value }))}
                className={inputClass}
              >
                <option value="">Select…</option>
                {(tutorsData?.items ?? []).map((tutor) => (
                  <option key={tutor.userId} value={tutor.userId}>
                    {tutor.name} ({tutor.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dm-navy">Mode</label>
              <select
                required
                value={form.mode}
                onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value as DeliveryMode }))}
                className={inputClass}
              >
                <option value="">Select…</option>
                {DELIVERY_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-dm-navy">Capacity</label>
              <input
                type="number"
                min={1}
                required
                value={form.capacity}
                onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dm-navy">Duration (hours)</label>
              <input
                type="number"
                min={1}
                required
                value={form.durationHours}
                onChange={(e) => setForm((f) => ({ ...f, durationHours: e.target.value }))}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dm-navy">Price (KES)</label>
              <input
                type="number"
                min={0}
                required
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                className={inputClass}
              />
            </div>
          </div>

          {createError && <p className="mt-3 text-sm text-red-600">{createError}</p>}

          <div className="mt-4">
            <button
              type="submit"
              disabled={creating}
              className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
            >
              {creating ? "Creating…" : "Create course"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
